import React from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Heart, Droplet, Activity, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Facilities = () => {
  const gymPhotos = [
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/dicl0r9b_IMG_7133.jpeg',
      title: 'Zone Fonctionnelle',
      description: 'Espace d\'entraînement fonctionnel complet'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/3c2x2xi9_IMG_7134.jpeg',
      title: 'Machines de Musculation',
      description: 'Équipements professionnels dernière génération'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/8txku760_IMG_7135.jpeg',
      title: 'Zone Cardio',
      description: 'Tapis de course, vélos et équipements cardio'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/bicpjhch_IMG_7136.jpeg',
      title: 'Équipements Complets',
      description: 'Salle équipée pour tous types d\'entraînements'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/w0e5qfr7_IMG_7137.jpeg',
      title: 'Zone Poids Libres',
      description: 'Large sélection d\'haltères et équipements'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/l6qsdr2p_IMG_7138.jpeg',
      title: 'Espace Coaching Privé',
      description: 'Zone dédiée aux séances personnalisées'
    }
  ];

  const recoveryPhotos = [
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/1l2vrzo0_IMG_7140.jpeg',
      title: 'Massage Professionnel',
      description: 'Table de massage pour récupération optimale'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_em-fitness/artifacts/undj9nyi_IMG_7141.jpeg',
      title: 'Sauna',
      description: 'Détente et récupération musculaire'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-black"></div>
        <div className="relative container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Nos <span className="text-red-600">Installations</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Une salle de sport complète et moderne équipée des meilleurs équipements 
            pour votre transformation physique et votre récupération
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-zinc-950 border-t border-red-600">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              {
                icon: Dumbbell,
                title: 'Équipements Pro',
                description: 'Machines et équipements de qualité professionnelle'
              },
              {
                icon: Activity,
                title: 'Espace Cardio',
                description: 'Zone dédiée aux exercices cardiovasculaires'
              },
              {
                icon: Heart,
                title: 'Zone Récupération',
                description: 'Massage professionnel et sauna disponibles'
              },
              {
                icon: Droplet,
                title: 'Hygiène Parfaite',
                description: 'Installation entretenue quotidiennement'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-zinc-900 p-8 rounded-lg text-center hover:bg-zinc-800 transition-all hover:scale-105"
              >
                <feature.icon className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gym Gallery */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-4">
            La <span className="text-red-600">Salle de Sport</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 text-lg">
            Équipements professionnels pour tous types d'entraînements
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gymPhotos.map((photo, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl aspect-[4/3]"
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{photo.title}</h3>
                    <p className="text-gray-300">{photo.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Details */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Équipements <span className="text-red-600">Disponibles</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-6">Musculation</h3>
                <ul className="space-y-3 text-gray-300 text-lg">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Racks de musculation professionnels</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Bancs de développé réglables</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Haltères de 1 à 50 kg</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Barres olympiques et disques</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Machines guidées variées</span>
                  </li>
                </ul>
              </div>

              <div className="bg-zinc-900 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-6">Cardio & Fonctionnel</h3>
                <ul className="space-y-3 text-gray-300 text-lg">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Tapis de course professionnels</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Vélos spinning et elliptiques</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Zone d'entraînement fonctionnel</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Équipements CrossFit</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Sac de frappe et accessoires</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery Section */}
      <section className="py-20 bg-black border-t border-red-600">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-white text-center mb-4">
            Zone <span className="text-red-600">Récupération</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 text-lg">
            Prenez soin de votre corps avec nos installations de récupération professionnelles
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {recoveryPhotos.map((photo, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl"
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-3xl font-bold text-white mb-3">{photo.title}</h3>
                    <p className="text-gray-300 text-lg">{photo.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 max-w-3xl mx-auto bg-zinc-900 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">
              Pourquoi la Récupération est Essentielle ?
            </h3>
            <p className="text-gray-300 text-lg text-center leading-relaxed">
              La récupération fait partie intégrante de votre progression. Le massage professionnel 
              aide à détendre les muscles, améliorer la circulation et prévenir les blessures. 
              Le sauna favorise l'élimination des toxines et accélère la récupération musculaire.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Venez Découvrir Nos Installations
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Réservez votre visite ou votre première séance d'essai
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-black hover:bg-zinc-900 text-white px-8 py-6 text-lg">
              Réserver une Visite
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
